<template>
  <div v-if="visible">
    <div v-if="isBanner" class="cf-hero-banner overflow-hidden">
      <img :src="bannerUrl" :alt="bannerAlt" class="block w-full cf-hero-banner-img" />
    </div>
    <div
      v-else-if="isComposed"
      class="cf-hero relative overflow-hidden text-white py-10 px-5"
      :class="variant === 'mobile' ? 'cf-hero--mobile' : 'lg-py-14 lg-px-6'"
    >
      <div
        class="relative z-10 max-w-6xl mx-auto flex items-center justify-between gap-8 border border-six p-8 cf-hero-inner"
        :class="[
          circleUrls.length ? '' : 'justify-center',
          variant === 'mobile' ? 'cf-hero-inner--mobile flex-col p-6' : 'lg-p-10 flex-wrap',
        ]"
      >
        <div
          class="flex-1 min-w-0 cf-hero-copy"
          :class="[
            circleUrls.length ? 'max-w-lg' : 'max-w-3xl text-center mx-auto',
            variant === 'mobile' ? 'text-center max-w-none w-full' : '',
          ]"
        >
          <p v-if="kicker" class="text-xs uppercase tracking-widest text-white text-opacity-70 mb-3">
            {{ kicker }}
          </p>
          <h1
            v-if="title"
            class="font-extrabold leading-tight tracking-wide mb-4 cf-hero-title"
            :class="variant === 'mobile' ? 'text-4xl' : 'text-4xl sm-text-5xl lg-text-6xl'"
          >
            {{ title }}
          </h1>
          <p v-if="tagline" class="text-lg mb-3 text-white text-opacity-90">{{ tagline }}</p>
          <p v-if="hashtag" class="font-bold mb-6">{{ hashtag }}</p>
          <div
            v-if="buttons.length"
            class="flex flex-wrap gap-3"
            :class="(!circleUrls.length || variant === 'mobile') ? 'justify-center' : ''"
          >
            <template v-for="(btn, bIndex) in buttons" :key="'hb-' + bIndex">
              <button
                v-if="interactive && isHashUrl(btn.url)"
                type="button"
                class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold text-white cursor-pointer border-0"
                :class="buttonClass(bIndex)"
                @click="$emit('navigate', btn.url)"
              >
                {{ btn.label }}
              </button>
              <a
                v-else-if="interactive && btn.url && !isHashUrl(btn.url)"
                :href="btn.url"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold text-white no-underline"
                :class="buttonClass(bIndex)"
              >
                {{ btn.label }}
              </a>
              <span
                v-else
                class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold text-white"
                :class="buttonClass(bIndex)"
              >
                {{ btn.label }}
              </span>
            </template>
          </div>
        </div>
        <div
          v-if="circleUrls.length"
          class="flex-1 min-w-0 grid grid-cols-2 gap-4 justify-items-center max-w-xs mx-auto cf-hero-portraits"
          aria-hidden="true"
        >
          <div
            v-for="(url, index) in circleUrls"
            :key="'hc-' + index"
            class="cf-hero-circle rounded-full overflow-hidden grayscale shadow-xl border-4 border-white border-opacity-20"
            :class="[
              portraitOffsetClass(index),
              variant === 'mobile' ? 'w-24 h-24' : 'w-28 h-28 sm-w-32 sm-h-32',
              interactive ? 'cf-hero-circle--interactive' : '',
            ]"
          >
            <img :src="url" alt="" class="cf-hero-circle-img" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CareerFairHeroBlock",
  props: {
    layout: { type: String, default: "composed" },
    bannerUrl: { type: String, default: "" },
    bannerAlt: { type: String, default: "Career fair banner" },
    kicker: { type: String, default: "" },
    title: { type: String, default: "" },
    tagline: { type: String, default: "" },
    hashtag: { type: String, default: "" },
    buttons: { type: Array, default: function () { return []; } },
    circleUrls: { type: Array, default: function () { return []; } },
    variant: { type: String, default: "desktop" },
    interactive: { type: Boolean, default: true },
  },
  computed: {
    isBanner() {
      return this.layout === "banner" && !!this.bannerUrl;
    },
    isComposed() {
      if (this.layout === "banner") return false;
      return !!(
        this.kicker ||
        this.title ||
        this.tagline ||
        this.hashtag ||
        this.buttons.length ||
        this.circleUrls.length
      );
    },
    visible() {
      return this.isBanner || this.isComposed;
    },
  },
  methods: {
    isHashUrl(url) {
      return !!(url && String(url).charAt(0) === "#");
    },
    buttonClass(index) {
      if (index === 0) return "cf-btn-gradient";
      return "bg-transparent border border-white border-opacity-50";
    },
    portraitOffsetClass(index) {
      if (this.variant === "mobile") return "";
      if (index === 1) return "mt-6";
      if (index === 2) return "-mt-2";
      if (index === 3) return "mt-4";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
.cf-hero-banner-img {
  display: block;
  width: 100%;
  max-height: 32rem;
  object-fit: cover;
}

.cf-hero {
  background:
    radial-gradient(ellipse at 70% 40%, rgba(73, 157, 212, 0.28) 0%, transparent 55%),
    linear-gradient(135deg, #212A3E 0%, #394867 55%, #0a4275 100%);
}

.cf-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0, transparent 2px),
    radial-gradient(circle at 60% 70%, rgba(255, 255, 255, 0.06) 0, transparent 2px),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0, transparent 2px);
  background-size: 120px 120px, 180px 180px, 90px 90px;
  opacity: 0.9;
  pointer-events: none;
  animation: cf-drift 28s linear infinite;
}

@keyframes cf-drift {
  from { transform: translateY(0); }
  to { transform: translateY(-40px); }
}

.cf-hero-circle-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.cf-hero-circle--interactive {
  transition: all 0.3s ease;
}

.cf-hero-circle--interactive:hover {
  filter: grayscale(0);
  transform: scale(1.05);
}

.cf-btn-gradient {
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
}

.cf-btn-gradient:hover {
  background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
}

.lg-py-14 {
  padding-top: 3.5rem;
  padding-bottom: 3.5rem;
}

.lg-px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.lg-p-10 {
  padding: 2.5rem;
}

.sm-text-5xl {
  font-size: 3rem;
}

.lg-text-6xl {
  font-size: 3.75rem;
}

.sm-w-32 {
  width: 8rem;
}

.sm-h-32 {
  height: 8rem;
}

.cf-hero-inner--mobile {
  text-align: center;
}

.cf-hero--mobile .cf-hero-portraits {
  margin-top: 0.5rem;
}
</style>
